import React, { useMemo, useEffect, useState } from "react";
import { FormLabel, Grid, InputBase, InputBaseProps } from "@mui/material";
import _ from "lodash";
import classNames from "classnames";
import { OCFormControl } from "./FormControl";
import { alpha, styled } from "@mui/material/styles";
import { FieldMetaProps } from "formik";
import { FieldError } from "./FieldError";

interface ITextProps {
  className?: string;
  onChangeDelay?: number;
  meta?: Partial<FieldMetaProps<any>>;
  id?: string;
  label?: string;
  InputProps?: any;
  labelSX?: any;
}

export type ITextFieldProps = ITextProps & InputBaseProps;

export const OCTextField: React.FC<ITextFieldProps> = styled((props: ITextFieldProps) => <InputBase {...props} />)(
  ({ theme }) => ({
    "& .error": {
      boxShadow: `${alpha(theme.palette.error.dark, 0.25)} 0 0 0 0.2rem !important`,
      transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
      borderColor: `${theme.palette.error.dark} !important`,
      backgroundColor: `${alpha(theme.palette.error.dark, 0.25)} !important`,
    },
    "& .MuiInputBase-input": {
      borderRadius: 4,
      position: "relative",
      backgroundColor: theme.custom.forms.inputBack,
      border: "1px solid",
      borderColor: theme.custom.forms.inputBorder,
      fontSize: 11,
      padding: "4px 6px",
      color: theme.palette.common.white,
      transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
      "&:focus, &:hover": {
        boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        borderColor: theme.palette.primary.main,
      },
      "&::placeholder, &::-webkit-input-placeholder": {
        color: theme.custom.forms.labels,
        opacity: 1,
      },
    },
  })
);

export const TextField = (props: ITextFieldProps): JSX.Element => {
  const { label, name, onFocus, onBlur, onChange, value, onChangeDelay, inputProps, disabled, meta, id, labelSX } =
    props;

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [internalValue, setInternalValue] = useState<string | number>("");

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  // If value is changed from outside, update internal controlled value
  useEffect(() => {
    const newValue = value === undefined || value === null ? "" : value;
    setInternalValue(newValue as string | number);
  }, [value]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setIsFocused(true);
    // Uses props onfocus (as formik doesn't supply one)
    if (onFocus && typeof onFocus === "function") {
      onFocus(e);
    }
  };

  // Custom blur handler for bug workaround due to input value
  // resetting when using search feature onBlur.
  const handleBlur = (e: React.FocusEvent<Element>): void => {
    e.persist();
    setIsFocused(false);
    setTimeout(() => {
      if (onBlur && typeof onBlur === "function") {
        onBlur(e as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
      }
    }, 1);
  };

  const handleInputOnchange = _.debounce(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      if (!event?.target) {
        return;
      }
      setInternalValue(event.target.value);
      if (onChange && typeof onChange === "function") {
        onChange(event);
      }
    },
    onChangeDelay !== undefined ? onChangeDelay : 100,
    {
      leading: true,
      trailing: false,
    }
  );
  const hasError = meta?.error ? true : false;
  return (
    <OCFormControl error={hasError} sx={{ width: "100%", my: 0.3 }}>
      <Grid container sx={{ display: "flex", alignItems: "start" }}>
        {label && (
          <Grid
            item
            sx={{
              display: "flex",
              justifyContent: hasError ? "space-between" : "flex-end",
              textAlign: "right",
            }}
            xs
          >
            <FieldError meta={meta} icon={true} />
            <FormLabel sx={{ ...labelSX }}>{label}</FormLabel>
          </Grid>
        )}
        <Grid item xs={label ? 7 : 12}>
          <OCTextField
            {...props}
            inputProps={{
              className: classNames(
                disabled && "disabled",
                isFocused && "focused",
                dirty && "dirty",
                hasError && "error"
              ),
              id: id ? id : name ? name + "_input" : "",
              ...inputProps,
            }}
            disabled={disabled}
            value={internalValue}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChange={handleInputOnchange}
            fullWidth={props.fullWidth !== undefined ? props.fullWidth : true}
            autoComplete="off"
            autoCorrect="off"
            placeholder={props.placeholder}
            autoCapitalize="off"
            spellCheck="false"
          />
        </Grid>
      </Grid>
    </OCFormControl>
  );
};
